import { useState, useEffect } from 'react';
import { collection, query, where, getDocs, limit } from 'firebase/firestore';
import { db } from '../firebase';
import { PLATFORM_COLLECTIONS } from '../utils/platformSchema';

/**
 * Load recent activity items for the instructor dashboard feed.
 * Reads from retentionActions + clientNotes written by Pass 36 apply actions.
 *
 * @param {string|null} instructorId
 * @param {string|null} orgId
 * @param {number} [maxItems=10]
 * @returns {{ items: object[], loading: boolean }}
 */
export function useInstructorActivityFeed(instructorId, orgId, maxItems = 10) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!instructorId) return;
    let cancelled = false;
    setLoading(true);

    async function load() {
      try {
        const allItems = [];

        // 1. Retention actions (follow-ups sent, session summaries)
        if (orgId) {
          try {
            const raRef = collection(db, PLATFORM_COLLECTIONS.RETENTION_ACTIONS);
            const raQ = query(raRef, where('instructorId', '==', instructorId), limit(20));
            const raSnap = await getDocs(raQ);
            raSnap.docs.forEach(d => {
              const data = d.data();
              allItems.push({
                id: d.id,
                type: data.actionType === 'session_followup_sent' ? 'followup' : 'retention',
                icon: data.actionType === 'session_followup_sent' ? '💬' : '🔄',
                text: formatRetentionText(data),
                meta: formatTime(data.createdAt),
                badge: data.actionType === 'session_followup_sent' ? 'Sent' : 'Logged',
                badgeType: 'green',
                timestamp: toMs(data.createdAt),
              });
            });
          } catch (_) {}
        }

        // 2. Client notes written by instructor (AI review summaries, progress notes)
        if (orgId) {
          try {
            const cnRef = collection(db, PLATFORM_COLLECTIONS.CLIENT_NOTES);
            const cnQ = query(cnRef, where('instructorId', '==', instructorId), limit(20));
            const cnSnap = await getDocs(cnQ);
            cnSnap.docs.forEach(d => {
              const data = d.data();
              const tags = data.tags || [];
              if (tags.includes('ai_review_summary')) {
                allItems.push({
                  id: d.id, type: 'review',
                  icon: '✓', text: 'You saved a session review',
                  meta: formatTime(data.createdAt), badge: 'Review', badgeType: 'green',
                  timestamp: toMs(data.createdAt),
                });
              } else if (tags.includes('ai_progress_summary')) {
                allItems.push({
                  id: d.id, type: 'progress',
                  icon: '📊', text: 'Progress summary saved',
                  meta: formatTime(data.createdAt), badge: 'Progress', badgeType: 'blue',
                  timestamp: toMs(data.createdAt),
                });
              } else if (tags.includes('ai_next_step')) {
                allItems.push({
                  id: d.id, type: 'recommendation',
                  icon: '🎯', text: 'Next step recommendation saved',
                  meta: formatTime(data.createdAt), badge: 'Saved', badgeType: 'purple',
                  timestamp: toMs(data.createdAt),
                });
              }
            });
          } catch (_) {}
        }

        // 3. Videos reviewed (completed reviews)
        try {
          const vRef = collection(db, 'videos');
          const vQ = query(vRef, where('assignedTo', '==', instructorId), where('status', '==', 'reviewed'), limit(10));
          const vSnap = await getDocs(vQ);
          vSnap.docs.forEach(d => {
            const data = d.data();
            allItems.push({
              id: d.id, type: 'video_review',
              icon: '🎬', text: `Reviewed video: ${data.title || 'Untitled'}`,
              meta: formatTime(data.updatedAt || data.createdAt),
              badge: 'Reviewed', badgeType: 'green',
              timestamp: toMs(data.updatedAt || data.createdAt),
            });
          });
        } catch (_) {}

        if (cancelled) return;

        // Sort by timestamp descending, take top N
        allItems.sort((a, b) => b.timestamp - a.timestamp);
        setItems(allItems.slice(0, maxItems));
      } catch (_) {
        if (!cancelled) setItems([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => { cancelled = true; };
  }, [instructorId, orgId, maxItems]);

  return { items, loading };
}

// ── Helpers ──

function formatRetentionText(data) {
  if (data.actionType === 'session_followup_sent') return 'Follow-up message sent via session summary';
  if (data.actionType === 'followup_sent') return 'Follow-up message sent';
  return `Retention action: ${(data.actionType || '').replace(/_/g, ' ')}`;
}

function formatTime(ts) {
  if (!ts) return '';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  const now = new Date();
  const diffH = (now - d) / (1000 * 60 * 60);
  if (diffH < 1) return 'Just now';
  if (diffH < 24) return `${Math.round(diffH)}h ago`;
  if (diffH < 48) return 'Yesterday';
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}

function toMs(ts) {
  if (!ts) return 0;
  if (ts.toDate) return ts.toDate().getTime();
  return new Date(ts).getTime() || 0;
}
